import { store as coreStore } from '@wordpress/core-data';
import { useSelect } from '@wordpress/data';
import { useEffect } from '@wordpress/element';

const { featuredImageVariationsConfig: FEATURED_IMAGE_VARIATIONS_CONFIG } =
  window.mediaPressMediaBlocks;

/**
 * Replace an image in the DOM with another one.
 *
 * This method is used since we can't/don't want to entirely replace the block edit component.
 *
 * @param  {React.Ref} wrapperRef      A ref to an element wrapping the target img element
 * @param  {Object}    originalImageId The ID of the original image
 * @param  {string}    variationKey    The key of the new image variation
 * @param  {Object}    variation       The new image variation
 * @return {void}
 */
const useDomImageReplace = (wrapperRef, originalImageId, variationKey, variation) => {
  const originalImage = useSelect((select) => select(coreStore).getMedia(originalImageId));
  const newImage = useSelect((select) => select(coreStore).getMedia(variation?.id));

  useEffect(() => {
    /**
     * Get the CSS styles for the crop.
     *
     * @param  {Crop}   crop The crop object
     * @return {string}      The CSS styles
     */
    function getCropStyles({ x, y, width, height }) {
      const top = y * 100;
      const right = (1 - x - width) * 100;
      const bottom = (1 - y - height) * 100;
      const left = x * 100;

      const configItem = FEATURED_IMAGE_VARIATIONS_CONFIG[variationKey];

      /**
       * Note: object-view-box is not yet supported in all browsers
       *
       * @see https://caniuse.com/?search=object-view-box
       */
      const styles = [
        `aspect-ratio: ${configItem.aspectRatio}`,
        `object-view-box: inset(${top}% ${right}% ${bottom}% ${left}%)`,
      ];

      return styles.join('; ');
    }

    /**
     * Callback to replace the image in the DOM.
     */
    function replaceImage() {
      if (originalImageId === variation?.id) {
        return;
      }

      const imgElement = wrapperRef.current?.querySelector('img');

      if (!imgElement) {
        return;
      }

      if (!imgElement.classList.contains('mediapress-media-featured-image-variation')) {
        imgElement.classList.add('mediapress-media-featured-image-variation');
      }

      if (newImage?.source_url) {
        imgElement.src = newImage?.source_url;
      } else {
        imgElement.src = originalImage?.source_url;
      }

      if (variation?.crop) {
        const cropStyles = getCropStyles(variation.crop);
        imgElement.style.cssText = cropStyles;
      } else {
        imgElement.style.cssText = null;
      }
    }

    /**
     * Mutation observer will trigger the image replacement whenever the block is updated.
     */
    const observer = new MutationObserver(() => {
      replaceImage();
    });

    if (wrapperRef.current) {
      observer.observe(wrapperRef.current, {
        childList: true,
        subtree: true,
      });

      replaceImage();
    }

    return () => observer.disconnect();
  }, [wrapperRef, variationKey, variation, originalImageId, originalImage, newImage]);
};

export default useDomImageReplace;
